import { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiPlus, FiMinus, FiX } from 'react-icons/fi';
import { Overlay, Popover, Modal } from 'react-bootstrap';
import { snakeCase, union } from 'lodash-es';
import classNames from 'classnames';
import { FilterOption } from 'src/types';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { useTranslations } from 'next-intl';

const Line: React.FC<{
  option: FilterOption;
  inputType: 'checkbox' | 'radio';
  className?: string;
  currentValue?: string | number | string[] | number[] | (number | string | undefined)[];
  inputClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isRoot?: boolean;
  prefixFieldName?: string;
  fieldName: string;
  setCurrentValue: React.Dispatch<React.SetStateAction<{ [key: string]: ItemValue }>>
}> = ({
  option,
  inputType,
  className,
  onChange,
  inputClassName,
  isRoot,
  currentValue,
  prefixFieldName,
  fieldName,
  setCurrentValue
}) => {
  const id = `${prefixFieldName ? `${prefixFieldName}_` : ''}${snakeCase(option.label)}_${snakeCase(
    String(option.value)
  )}`;
  const name = `${prefixFieldName ? `${prefixFieldName}_` : ''}${fieldName}`;

  const isChecked = currentValue
    ? typeof currentValue !== 'object'
      ? String(currentValue) === String(option.value)
      : 'includes' in currentValue
      ? (currentValue.map(v => String(v)) as (string | number)[]).includes(String(option.value))
      : false
    : false;

  return (
    <label className={`tw-flex tw-cursor-pointer tw-items-start ${className || ''}`}>
      <div className="form-check tw-mr-2">
        <input
          type={inputType}
          name={name}
          data-root={isRoot ? 1 : undefined}
          className={`form-check-input ${inputClassName || ''}`}
          id={id}
          value={option.value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCurrentValue((prev) => {
              const aryValue =
                inputType === 'checkbox' ? ((prev[fieldName] || []) as (string | number)[]) : [];
              const filteredValue = aryValue.filter((v) => String(v) !== String(option.value));
              const newValue =
                inputType === 'radio'
                  ? e.target.checked
                    ? String(option.value)
                    : undefined
                  : inputType === 'checkbox'
                  ? e.target.checked
                    ? aryValue.concat([String(option.value)])
                    : filteredValue.length > 0
                    ? filteredValue
                    : undefined
                  : undefined;
              return {
                ...prev,
                [fieldName]: newValue,
              };
            });
            typeof onChange === 'function' && onChange(e);
          }}
          checked={isChecked}
        />
        <label className="form-check-label" htmlFor={id}></label>
      </div>
      <div className="">
        <div className="tw-text-brand-navy tw-text-sm">{option.label}</div>
        {option.desc ? <div className="tw-text-grey-light tw-text-sm">{option.desc}</div> : null}
      </div>
    </label>
  );
};

type ItemValue = string | number | undefined | (number | string | undefined)[];
type ControlButtonsProps = {
  fieldNames: string[];
  className?: string;
  setShowPopover: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentValue: React.Dispatch<React.SetStateAction<{ [key: string]: ItemValue }>>;
  defaultValue?: ItemValue;
  filter: () => void;
};
const ControlButtons: React.FC<ControlButtonsProps> = ({
  fieldNames,
  className,
  setShowPopover,
  setCurrentValue,
  defaultValue,
  filter
}) => {
  const [shouldFilter, setShouldFilter] = useState(false)
  const translation = useTranslations('components.filter.CommonItem')
  useEffect(() => {
    if (shouldFilter) {
      filter()
    }
  }, [shouldFilter])

  return (
    <div
      className={`tw-font-sans tw-w-full tw-bg-brand-yellow tw-flex tw-items-center tw-justify-between tw-text-sm ${
        className || ''
      }`}
    >
      <div
        className="tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-underline tw-cursor-pointer tw-lowercase"
        onClick={() => {
          setCurrentValue((prev) => ({
            ...prev,
            ...fieldNames.reduce((memo, key) => {
              memo[key] = defaultValue;
              return memo;
            }, {} as { [k: string]: ItemValue }),
          }));
          setShowPopover(false);
          setShouldFilter(true);
        }}
      >
        {translation('clear')}
      </div>
      <button
        className="btn btn-primary tw-cursor-pointer tw-text-sm"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (e.currentTarget instanceof HTMLButtonElement) {
            e.currentTarget.disabled = true;
          }
          setShowPopover(false);
          filter()
        }}
      >
        {translation('apply')}
      </button>
    </div>
  );
};

export const ItemInner: React.FC<
  Omit<Props, 'label'> & {
    currentValue?: ItemValue;
    prefixFieldName?: string;
    setCurrentValue: React.Dispatch<React.SetStateAction<{ [key: string]: ItemValue }>>
  }
> = ({
  isNested,
  options,
  inputType,
  maxNumShownOption,
  currentValue,
  fieldName,
  prefixFieldName,
  setCurrentValue
}) => {
  const translation = useTranslations('components.filter.CommonItem')
  const checkboxClass = prefixFieldName
    ? `js-${prefixFieldName}-checbox-indeterminate`
    : 'js-checkbox-indeterminate';
  const containerClass = prefixFieldName
    ? `js-${prefixFieldName}-container-checbox-indeterminate`
    : 'js-container-checkbox-indeterminate';
  const childCheckboxClass = prefixFieldName
    ? `js-${prefixFieldName}-child-checbox-indeterminate`
    : 'js-child-checkbox-indeterminate';
  const rowCheckboxClass = prefixFieldName
    ? `js-${prefixFieldName}-row-checbox-indeterminate`
    : 'js-row-checkbox-indeterminate';
  const [showAll, setShowAll] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<{ [k: string]: boolean }>({});
  const handleChangeIndeterminateCheckbox = (
    e: React.ChangeEvent<HTMLInputElement>,
    optionValue: string | number
  ) => {
    const checkboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll(`.${checkboxClass}`);
    let check: HTMLInputElement | null | undefined = e.target;

    //  exit if change event did not come from
    //  our list of indeterminateCheckboxes
    if (Array.from(checkboxes).indexOf(check) === -1) return;

    //  check/unchek children (includes check itself)
    let children = check.dataset.root
      ? check
          .closest(`.${containerClass}`)
          ?.querySelector(`.${childCheckboxClass}`)
          ?.querySelectorAll('input') || []
      : check.closest(`.${rowCheckboxClass}`)?.querySelectorAll('input') || [];

    let childValues = Array.from(children).map((o) => o.value);
    const checked = check?.checked;

    setCurrentValue((prev) => {
      const safeCurrentValue = (Array.isArray(prev[fieldName]) ? prev[fieldName] : []) as (number | string | undefined)[];
      return {
        ...prev,
        [fieldName]: checked
          ? union(safeCurrentValue, childValues)
          : safeCurrentValue.filter((v) => !childValues.includes(String(v)))
      }
    })

    children.forEach(function (child) {
      child.checked = check?.checked || false;
    });

    //  traverse up from target check
    while (check) {
      //  find parent and sibling checkboxes (quick'n'dirty)
      let parent: HTMLInputElement | null | undefined = check
        .closest(`.${containerClass}`)
        ?.querySelector('input');
      let childCheckboxContainer = check
        .closest(`.${containerClass}`)
        ?.querySelector(`.${childCheckboxClass}`);
      let siblings: HTMLInputElement[] = parent
        ? Array.from(childCheckboxContainer?.querySelectorAll('input') || [])
        : [];

      //  get checked state of siblings
      //  are every or some siblings checked (using Boolean as test function)
      let checkStatus = siblings.map(function (check) {
        return check?.checked || false;
      });
      let every = checkStatus.every(Boolean);
      let some = checkStatus.some(Boolean);

      //  check parent if all siblings are checked
      //  set indeterminate if not all and not none are checked
      if (parent) {
        parent.checked = every;
        const parentValue = parent.value;

        setCurrentValue((prev) => {
          const safeCurrentValue = (Array.isArray(prev[fieldName]) ? prev[fieldName] : []) as (number | string | undefined)[];

          return {
            ...prev,
            [fieldName]: checked
              ? union(safeCurrentValue, [parentValue])
              : safeCurrentValue.filter((v) => v !== parentValue)
          }
        })
        parent.indeterminate = !every && every !== some;
      }

      if (check.checked || check.indeterminate) {
        setActiveAccordion((prev) => ({ ...prev, [optionValue]: true }));
      } else {
        setActiveAccordion((prev) => ({ ...prev, [optionValue]: false }));
      }

      //  prepare for nex loop
      check = check != parent ? parent : undefined;
    }
  };

  return (
    <>
      {options.map((option, index) => (
        <div
          key={option.value}
          className={classNames('tw-font-sans', {
            'tw-hidden': !showAll && maxNumShownOption && index >= maxNumShownOption,
          })}
        >
          {isNested && option.children && option.children.length > 0 ? (
            <div className={containerClass}>
              <div className="tw-cursor-pointer tw-flex tw-justify-between tw-items-center">
                <Line
                  option={option}
                  inputType={inputType}
                  className={classNames(`${rowCheckboxClass}`, { 'tw-mt-2': index > 0 })}
                  inputClassName={checkboxClass}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeIndeterminateCheckbox(e, option.value)
                  }
                  isRoot
                  currentValue={currentValue}
                  fieldName={fieldName}
                  prefixFieldName={prefixFieldName}
                  setCurrentValue={setCurrentValue}
                />
                <div
                  className={classNames('tw-mb-2 tw-flex-grow text-right tw-cursor-pointer', { 'tw-mt-2': index > 0 })}
                  onClick={() => {
                    setActiveAccordion((prev) => ({
                      ...prev,
                      [option.value]: !prev[option.value],
                    }));
                  }}
                >
                  {activeAccordion[option.value] ? (
                    <FiMinus
                      size={24}
                      strokeWidth={1}
                      className={classNames('tw-text-brand-navy')}
                    />
                  ) : (
                    <FiPlus
                      size={24}
                      strokeWidth={1}
                      className={classNames('tw-text-brand-navy')}
                    />
                  )}
                </div>
              </div>
              <div
                className={`${childCheckboxClass} tw-pl-6 ${
                  activeAccordion[option.value] ? '' : 'tw-hidden'
                }`}
              >
                {option.children.map((co) => (
                  <Line
                    key={co.value}
                    currentValue={currentValue}
                    fieldName={fieldName}
                    prefixFieldName={prefixFieldName}
                    option={co}
                    inputType={inputType}
                    className={rowCheckboxClass}
                    inputClassName={checkboxClass}
                    setCurrentValue={setCurrentValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeIndeterminateCheckbox(e, option.value)
                    }
                  />
                ))}
              </div>
            </div>
          ) : (
            <Line
              option={option}
              inputType={inputType}
              className={classNames({ 'tw-mt-2': index > 0 })}
              currentValue={currentValue}
              fieldName={fieldName}
              prefixFieldName={prefixFieldName}
              setCurrentValue={setCurrentValue}
            />
          )}
        </div>
      ))}

      {maxNumShownOption && options.length > maxNumShownOption ? (
        <div
          className="tw-mt-4 tw-text-grey tw-text-sm tw-font-sans tw-cursor-pointer tw-underline"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {translation('show')} {showAll ? translation('less') : translation('more')}
        </div>
      ) : null}
    </>
  );
};

type Props = {
  inputType: 'checkbox' | 'radio';
  fieldName: string;
  label: string;
  options: FilterOption[];
  maxNumShownOption?: number;
  isNested?: boolean;
  hideCounter?: boolean;
  prices?: number[];
  defaultValue?: ItemValue;
  onChange?: (values: ItemValue) => void;
  className?: string;
  defaultClassName?: string;
  selectedClassName?: string;
};

const Item: React.FC<Props> = ({
  label,
  fieldName,
  inputType,
  options,
  isNested,
  maxNumShownOption,
  hideCounter,
  defaultValue,
  onChange,
  className = 'md:tw-py-3 tw-py-2 tw-px-6 md:tw-px-8 tw-rounded-2xl tw-border tw-mx-2',
  defaultClassName = 'tw-bg-white tw-border-grey-lighter',
  selectedClassName = 'tw-bg-orange tw-text-white tw-border-orange'
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const { isScreenMd } = useBreakpoints();
  const [currentValue, setCurrentValue] = useState<{ [key: string]: ItemValue }>({ [fieldName]: defaultValue });
  const value = currentValue[fieldName]
  const numSelected =
    inputType === 'checkbox'
      ? ((value || []) as any[]).length
      : value
      ? 1
      : 0;

  const filter = () => {
    onChange && onChange(currentValue[fieldName])
  }

  return (
    <>
      <div
        ref={itemRef}
        className={classNames(
          `tw-flex tw-items-center tw-leading-none tw-cursor-pointer ${className}`,
          {
            [selectedClassName]: numSelected > 0,
            [defaultClassName]: numSelected <= 0,
          }
        )}
        onClick={() => setShow(true)}
      >
        <span className="tw-mr-1 tw-font-styleSans tw-font-semibold">
          <span dangerouslySetInnerHTML={{__html: label}} />
        </span>
        {!hideCounter && numSelected > 0 ? (
          <span className="tw-hidden tw-rounded-full tw-text-center md:tw-inline-block tw-text-xs tw-text-brand-navy tw-font-semibold tw-mr-1 tw-bg-white tw-w-4 tw-h-4">
            {numSelected}
          </span>
        ) : null}
        <FiChevronDown
          className="tw-text-brand-navy"
        />
      </div>

      {isScreenMd ? (
        <Overlay
          rootClose={true}
          target={itemRef}
          show={show}
          placement="bottom"
          onHide={() => {
            setShow(false);
            filter();
          }}
          popperConfig={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ],
          }}
        >
          <Popover
            id={`popover-filter-item-${snakeCase(label)}`}
            className={`tw-w-72 tw-py-3 tw-px-0 popover-no-arrow`}
          >
            <div className="md:tw-max-h-sm tw-overflow-y-auto tw-relative">
              <Popover.Content className="tw-px-3">
                <ItemInner
                  options={options}
                  inputType={inputType}
                  isNested={isNested}
                  maxNumShownOption={maxNumShownOption}
                  currentValue={currentValue[fieldName]}
                  fieldName={fieldName}
                  setCurrentValue={setCurrentValue}
                />
              </Popover.Content>
            </div>
            <ControlButtons
              setShowPopover={setShow}
              defaultValue={defaultValue}
              setCurrentValue={setCurrentValue}
              fieldNames={[fieldName]}
              className="tw-pt-3 tw-border-t tw-border-grey-lighter tw-px-3"
              filter={filter}
            />
          </Popover>
        </Overlay>
      ) : (
        <Modal
          show={show}
          onHide={() => {
            setShow(false);
            filter();
          }}
          dialogClassName="modal-dialog-bottom modal-almost-full-height--sp modal-dialog-scrollable modal-lg"
          contentClassName="tw-rounded-tl-2xl tw-rounded-tr-2xl"
        >
          <Modal.Header>
            <div className="modal-title tw-font-semibold"><span dangerouslySetInnerHTML={{__html: label}} /></div>
            <button type="button" className="close" onClick={() => setShow(false)}>
              <FiX strokeWidth={1} size={24} className="tw-text-brand-navy" />
            </button>
          </Modal.Header>
          <Modal.Body>
            <ItemInner
              options={options}
              inputType={inputType}
              isNested={isNested}
              maxNumShownOption={maxNumShownOption}
              currentValue={currentValue[fieldName]}
              fieldName={fieldName}
              setCurrentValue={setCurrentValue}
            />
          </Modal.Body>
          <Modal.Footer>
            <ControlButtons
              setShowPopover={setShow}
              defaultValue={defaultValue}
              setCurrentValue={setCurrentValue}
              fieldNames={[fieldName]}
              className="tw-pt-3 tw-border-t tw-border-grey-lighter tw-px-3"
              filter={filter}
            />
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default Item;
